import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiClient, handleApiError } from '@/config/api';
import { ShiftsResponse } from '@/utils/shifts';

export interface ScheduleEvent {
  id: number;
  name: string;
  eventDate: string;
  start: string;
  end: string;
  type: string;
  status?: string;
  department?: string;
  rate?: number;
  payType?: string;
  isRecurring?: boolean;
  adminNote?: string;
  displayTime?: string;
  displayDuration?: string;
}

interface EmployeeScheduleResponse {
  employeeId: number;
  employeeName: string;
  startDate: string | null;
  endDate: string | null;
  events: ScheduleEvent[] | null;
  totalEvents: number;
  totalScheduledMinutes: number;
  totalScheduledHours: string;
  daysWithSchedule: number;
  todaysEvents: ScheduleEvent[] | null;
  currentEvents: ScheduleEvent[] | null;
  hasScheduleToday: boolean;
  currentlyScheduled: boolean;
}

interface CreateExceptionEventRequest {
  name: string;
  eventDate: string;
  start: string;
  end: string;
  exceptionReason: string;
  dept?: string;
}

interface CreateExceptionEventResponse {
  success: boolean;
  message: string;
  eventId?: number;
  status?: string;
  event?: ScheduleEvent;
}

/** An exception event the employee submitted that is awaiting approval. */
export interface PendingException {
  eventId: number;
  name: string;
  eventDate: string;
  startTime: string;
  endTime: string;
  status: string;
  type: string;
  justification?: string;
  department?: string;
  durationMinutes: number;
  displayTime?: string;
  displayDuration?: string;
  statusMessage?: string;
}

interface ScheduleState {
  todaySchedule: EmployeeScheduleResponse | null;
  weekSchedule: EmployeeScheduleResponse | null;
  upcomingSchedule: EmployeeScheduleResponse | null;
  /** The Upcoming section loads on its own so a failure there never blanks "This week". */
  isLoadingUpcoming: boolean;
  upcomingError: string | null;
  todayShifts: ShiftsResponse | null;
  pendingExceptions: PendingException[];
  isLoading: boolean;
  isCreatingEvent: boolean;
  error: string | null;
}

const initialState: ScheduleState = {
  todaySchedule: null,
  weekSchedule: null,
  upcomingSchedule: null,
  isLoadingUpcoming: false,
  upcomingError: null,
  todayShifts: null,
  pendingExceptions: [],
  isLoading: false,
  isCreatingEvent: false,
  error: null,
};

// Async Thunks
export const getTodaySchedule = createAsyncThunk(
  'schedule/getTodaySchedule',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get<EmployeeScheduleResponse>('/schedule/today');
      return response;
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

export const getTodayShifts = createAsyncThunk(
  'schedule/getTodayShifts',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get<ShiftsResponse>('/schedule/shifts/today');
      return response;
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

export const getWeekSchedule = createAsyncThunk(
  'schedule/getWeekSchedule',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get<EmployeeScheduleResponse>('/schedule/week');
      return response;
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

export const getUpcomingSchedule = createAsyncThunk(
  'schedule/getUpcomingSchedule',
  async (days: number = 7, { rejectWithValue }) => {
    try {
      const response = await apiClient.get<EmployeeScheduleResponse>(
        `/schedule/upcoming?days=${days}`
      );
      return response;
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

export const getCurrentEvents = createAsyncThunk(
  'schedule/getCurrentEvents',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get<EmployeeScheduleResponse>('/schedule/current');
      return response;
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

export const getPendingExceptions = createAsyncThunk(
  'schedule/getPendingExceptions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get<PendingException[]>('/events/pending');
      return response;
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

export const createExceptionEvent = createAsyncThunk(
  'schedule/createExceptionEvent',
  async (request: CreateExceptionEventRequest, { rejectWithValue }) => {
    try {
      const response = await apiClient.post<CreateExceptionEventResponse>(
        '/schedule/exception-event',
        request
      );
      return response;
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

// Slice
const scheduleSlice = createSlice({
  name: 'schedule',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSchedule: (state) => {
      state.todaySchedule = null;
      state.weekSchedule = null;
      state.upcomingSchedule = null;
      state.todayShifts = null;
    },
  },
  extraReducers: (builder) => {
    // Today's Schedule
    builder
      .addCase(getTodaySchedule.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getTodaySchedule.fulfilled, (state, action) => {
        state.isLoading = false;
        state.todaySchedule = action.payload;
      })
      .addCase(getTodaySchedule.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Today's Shifts (grouped events + punches from the backend)
    builder
      .addCase(getTodayShifts.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getTodayShifts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.todayShifts = action.payload;
      })
      .addCase(getTodayShifts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Week Schedule
    builder
      .addCase(getWeekSchedule.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getWeekSchedule.fulfilled, (state, action) => {
        state.isLoading = false;
        state.weekSchedule = action.payload;
      })
      .addCase(getWeekSchedule.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Upcoming Schedule
    builder
      .addCase(getUpcomingSchedule.pending, (state) => {
        state.isLoadingUpcoming = true;
        state.upcomingError = null;
      })
      .addCase(getUpcomingSchedule.fulfilled, (state, action) => {
        state.isLoadingUpcoming = false;
        state.upcomingSchedule = action.payload;
      })
      .addCase(getUpcomingSchedule.rejected, (state, action) => {
        state.isLoadingUpcoming = false;
        state.upcomingError = action.payload as string;
      });

    // Current Events
    builder.addCase(getCurrentEvents.fulfilled, (state, action) => {
      // Update today's schedule with current events
      if (state.todaySchedule) {
        state.todaySchedule.currentEvents = action.payload.currentEvents;
        state.todaySchedule.currentlyScheduled = action.payload.currentlyScheduled;
      }
    });

    // Pending Exception Requests
    builder
      .addCase(getPendingExceptions.fulfilled, (state, action) => {
        state.pendingExceptions = action.payload;
      })
      .addCase(getPendingExceptions.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Create Exception Event
    builder
      .addCase(createExceptionEvent.pending, (state) => {
        state.isCreatingEvent = true;
        state.error = null;
      })
      .addCase(createExceptionEvent.fulfilled, (state) => {
        state.isCreatingEvent = false;
        // Note: Refresh schedule data in component after successful creation
      })
      .addCase(createExceptionEvent.rejected, (state, action) => {
        state.isCreatingEvent = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearSchedule } = scheduleSlice.actions;
export default scheduleSlice.reducer;
